import { useState } from 'react';
import useSettings from '../../hooks/useSettings';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import useTelaGrande from '../../hooks/useTelaGrande';
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import AvisoSistema from './AvisoSistema';
import BotaoMensagemFlutuante from './BotaoMensagemFlutuante';
import Assistente from '../../components/gps/Assistente';
import RodapeDashboard from './RodapeDashboard';
import BarraAbas from './abas/BarraAbas';
import { AreaTelas } from './abas/AbasContexto';

/* ============================================================================
   Shell do dashboard - visual novo (shadcn + Tailwind).

   Substitui o `styled('main')` do MUI que calculava padding e largura a partir de
   `HEADER`/`NAVBAR` e de `theme.breakpoints`.

   A IDEIA CENTRAL: existe UMA variavel, `--gps-lateral`, com a largura ocupada
   pela sidebar no momento. A faixa superior usa ela em `left`, o conteudo usa em
   `margin-left`. Antes a mesma conta estava escrita em dois arquivos com valores
   diferentes (o header somava `calc(100%)`, o main subtraia
   `NAVBAR.DASHBOARD_WIDTH`), e recolher a barra deixava faixa e conteudo
   desalinhados.

   O `min-h-screen` + `flex-col` + rodape com `mt-auto` continuam sendo o que
   empurra o rodape para o fim da TELA em pagina curta, sem `position: fixed` (que
   cobriria a ultima linha das tabelas). O comentario original dizia isso e a
   razao continua valendo.

   Os dois modos de layout do template seguem funcionando:
     - `themeLayout === 'vertical'`  -> menu HORIZONTAL abaixo da faixa (sem sidebar)
     - qualquer outro valor (padrao) -> sidebar a esquerda
   Os nomes sao invertidos no template e nao foram trocados de proposito: mudar
   isso agora renomearia um valor que ja esta gravado no cookie dos usuarios.
   ============================================================================ */

export default function DashboardLayout({ children }) {
  const { isCollapse } = useCollapseDrawer();
  const { themeLayout, themeStretch } = useSettings();
  const isDesktop = useTelaGrande();

  const [open, setOpen] = useState(false);

  const verticalLayout = themeLayout === 'vertical';

  // Sem sidebar (mobile ou layout horizontal) a lateral e zero.
  const lateral = verticalLayout || !isDesktop ? '0px' : isCollapse ? '72px' : 'var(--gps-sidebar-w)';

  /* --------------------------------------------------------------------------
     Largura do conteudo (opcao "Largura" do painel de aparencia).

     PRIMEIRA TENTATIVA ESTAVA ERRADA e vale registrar: eu envolvi o conteudo num
     wrapper com `max-width` e deixei "total" como `none`. So que as telas tem
     `max-w-[1560px]` PROPRIO - com o wrapper em `none`, quem manda continua sendo
     o limite de dentro, e "largura total" nao alargava nada. O usuario marcava
     total e a tela nao se movia.

     Agora e o contrario: em vez de tentar apertar de FORA, o shell publica a
     variavel `--gps-pagina-max` e cada tela le ela no proprio container, com o
     numero que ela mesma escolheu como FALLBACK:

       total    -> `none`     : desliga o limite de dentro, a tela ocupa a janela
       limitada -> nao define : cada tela usa o maximo que foi desenhado para ela
                                (1560 nas listas, 1100 em configurar, 1000 em
                                novidades) - por isso "limitada" nao achata tudo
                                num numero unico.

     Uma variavel, um lugar para a regra, e nenhuma tela precisou saber da opcao.
     -------------------------------------------------------------------------- */
  // A variavel e SEMPRE definida - nao ha fallback na classe porque a virgula de
  // `var(--x, 1560px)` dentro de valor arbitrario do Tailwind estoura o parser de
  // CSS do build ("`,` or `)` expected"). Aprendido na pratica.
  const varLargura = { '--gps-pagina-max': themeStretch ? 'none' : '1560px' };

  if (verticalLayout) {
    return (
      <div className="min-h-screen bg-fundo" style={{ '--gps-lateral': '0px', ...varLargura }}>
        <DashboardHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />

        {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
        )}

        {/* A faixa de aviso fica no inicio do <main>, EMPURRANDO o conteudo: ela
            existe justamente para ser vista, e sobreposta taparia a primeira
            linha da tela. Nos dois ramos do layout - foi esquecer o segundo ramo
            que deixou o menu horizontal sem rodape por muito tempo. */}
        <main
          className="flex min-h-screen flex-col px-2 pb-0"
          style={{
            // No desktop a faixa de menu horizontal fica logo abaixo da superior,
            // por isso o dobro da altura.
            paddingTop: isDesktop ? 'calc(var(--gps-topbar-h) * 2 + 8px)' : 'calc(var(--gps-topbar-h) + 16px)',
          }}
        >
          <AvisoSistema />
          <BarraAbas />

          <AreaTelas>{children}</AreaTelas>

          {/* Precisa estar nos DOIS ramos do layout - foi o descuido que deixou o
              NavbarHorizontal sem rodape por muito tempo. */}
          <div className="mt-auto">
            <RodapeDashboard />
          </div>
        </main>

        <BotaoMensagemFlutuante />
        <Assistente />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fundo" style={{ '--gps-lateral': lateral, ...varLargura }}>
      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />

      <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

      <main
        className="flex min-h-screen flex-col transition-[margin-left] duration-200"
        style={{
          marginLeft: 'var(--gps-lateral)',
          paddingTop: 'calc(var(--gps-topbar-h) + 8px)',
        }}
      >
        <AvisoSistema />
        <BarraAbas />

        <AreaTelas>{children}</AreaTelas>

        <div className="mt-auto">
          <RodapeDashboard />
        </div>
      </main>

      <BotaoMensagemFlutuante />
      <Assistente />
    </div>
  );
}
